import Config, { SSO_META } from "config";
import dayjs from "dayjs";
import {
  AuthInterface,
  BarrackType,
  BombardType,
  CampType,
  Chain,
  LoginType,
  MongenRace,
  NftPoint,
  NFTType,
  PlotType,
  Rarity,
  RewardEntity,
  RewardType,
  TicketRarity,
  TicketUnit,
  TowerType,
  TrapType,
  UserEntity,
} from "interfaces";
import _ from "lodash";
import { store } from "reducer/store";

dayjs.extend(require("dayjs/plugin/utc"));

function create_UUID() {
  var dt = new Date().getTime();
  var uuid = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
    /[xy]/g,
    function (c) {
      var r = (dt + Math.random() * 16) % 16 | 0;
      dt = Math.floor(dt / 16);
      return (c == "x" ? r : (r & 0x3) | 0x8).toString(16);
    }
  );
  return uuid;
}

function displayWalletAddress(address: string) {
  if (!address) return "--";
  return address.slice(0, 5) + "..." + address.slice(address.length - 4);
}

function convertToInternationalCurrencySystem(labelValue: number, digits = 2) {
  if (!labelValue && labelValue != 0) return null;
  let value = Math.abs(Number(Number(labelValue).toFixed(13)));

  let digitValue = 100;
  switch (digits) {
    case 2:
      digitValue = 1.0e2;
      break;
    case 3:
      digitValue = 1.0e3;
      break;
    case 4:
      digitValue = 1.0e4;
      break;
    case 5:
      digitValue = 1.0e5;
      break;
    case 6:
      digitValue = 1.0e6;
      break;
    case 7:
      digitValue = 1.0e7;
      break;
    case 8:
      digitValue = 1.0e8;
      break;
  }

  if (value >= 1.0e9) {
    value = value / 1.0e9;
    value = Math.floor(Number(value * digitValue)) / digitValue;
    value = toFixed(value);
    return value + "B";
  } else if (value >= 1.0e6) {
    value = value / 1.0e6;
    value = Math.floor(Number(value * digitValue)) / digitValue;
    value = toFixed(value);
    return value + "M";
  } else if (value >= 1.0e3) {
    value = value / 1.0e3;
    value = Math.floor(Number(value * digitValue)) / digitValue;
    value = toFixed(value);
    return value + "K";
  } else {
    value = value / 1;
    value = Math.floor(Number(value * digitValue)) / digitValue;
    value = toFixed(value);
    return value;
  }
  // // Nine Zeroes for Billions
  // return Math.abs(Number(labelValue)) >= 1.0e9
  //   ? (Math.abs(Number(labelValue)) / 1.0e9).toFixed(2) + "B"
  //   : // Six Zeroes for Millions
  //   Math.abs(Number(labelValue)) >= 1.0e6
  //   ? (Math.abs(Number(labelValue)) / 1.0e6).toFixed(2) + "M"
  //   : // Three Zeroes for Thousands
  //   Math.abs(Number(labelValue)) >= 1.0e3
  //   ? (Math.abs(Number(labelValue)) / 1.0e3).toFixed(2) + "K"
  //   : Math.abs(Number(labelValue)).toFixed(2);
}

function displayFloat(f: number, fixed = 6, showDot = true) {
  if (isNaN(f) || f == 0) {
    return "--";
  }
  return Number(f).toFixed(fixed) + (showDot ? "..." : "");
}

function getKeyMap(
  field: any,
  modelFields: string[],
  data: any,
  getKey?: boolean
) {
  let key = "";
  let index = _.findIndex(
    modelFields,
    (e) => {
      return e === field;
    },
    0
  );
  if (index < 26) {
    key = String.fromCharCode(65 + index);
  } else {
    key = `${String.fromCharCode(
      65 + Math.floor(index / 26) - 1
    )}${String.fromCharCode(65 + (index % 26))}`;
  }
  if (getKey) {
    return key;
  }
  if (!data || data[key] === undefined || data[key] === null) {
    return null;
  } else {
    return data[key];
  }
}

function getExplorerUrl({
  address,
  loginType,
  testnet,
}: {
  address: string;
  loginType: LoginType;
  testnet: boolean;
}) {
  switch (loginType) {
    case LoginType.BSC:
    default:
      return `https://${
        testnet ? "testnet" : "mainet"
      }.bscscan.com/address/${address}`;
    case LoginType.Terra:
      return `https://finder.terra.money/${
        testnet ? "testnet" : "mainet"
      }/address/${address}`;
    case LoginType.Avalanche:
      return `https://${
        testnet ? "testnet." : ""
      }snowtrace.io/address/${address}`;
    case LoginType.Okex:
      return `https://www.okx.com/explorer/${
        testnet ? "oktc-test" : "oktc"
      }/address/${address}`;
    case LoginType.Aura:
      return `https://${
        testnet ? "euphoria." : ""
      }aurascan.io/account/${address}`;
  }
}

function getRarityName(rarity: Rarity) {
  switch (rarity) {
    case Rarity.Common:
      return "Common";
    case Rarity.Uncommon:
      return "Uncommon";
    case Rarity.Rare:
      return "Rare";
    case Rarity.Epic:
      return "Epic";
    case Rarity.Legendary:
      return "Legendary";
    default:
      return "";
  }
}

function getTicketRarityName(rarity: TicketRarity) {
  switch (rarity) {
    case TicketRarity.Common:
      return "Common";
    case TicketRarity.Uncommon:
      return "Uncommon";
    case TicketRarity.Rare:
      return "Rare";
    case TicketRarity.Epic:
      return "Epic";
    case TicketRarity.Legendary:
      return "Legendary";
    case TicketRarity.GE:
      return "General";
    case TicketRarity.GL:
      return "Global";
    default:
      return "";
  }
}

function getRewardName(reward: RewardEntity) {
  switch (reward.reward_type) {
    case RewardType.LOCK_MAG:
      return "sMAG";
    case RewardType.UNLOCK_MAG:
      return "MAG";
    case RewardType.MSTR:
      return "MSTR";
    case RewardType.BOX:
      return "Box";
    case RewardType.FOOD:
      return "Food";
    case RewardType.PLOT:
      switch (reward.plot_type) {
        case PlotType.LandCore:
          return "Landcore";
        case PlotType.Pasture:
          return "Habitat";
        case PlotType.Breeding:
          return "Breeding den";
        case PlotType.Hatching:
          return "Hatchery";
        case PlotType.Production:
          return "Farm";
        case PlotType.Storage:
          return "Food storage";
        case PlotType.Barracks:
          return "Training Ground";
        case PlotType.Bombard:
          return "Turret";
        case PlotType.Tower:
          return "Watchtower";
        case PlotType.Trap:
          return "Landmine";
        case PlotType.Decor:
          return "Decor";
        case PlotType.Camp:
          return "Military Camp";
        default:
          return "Plot";
      }
    case RewardType.SOULCORE:
      return "Soulcore";
    case RewardType.EXP:
      return "EXP";
    case RewardType.EVOLUTION_ITEM:
      return "Evolution Item";
    case RewardType.TICKET:
      return `${Rarity[reward.rarity ?? 0]} Ticket`;
    case RewardType.NormalStone:
      return "Normal Stone";
    case RewardType.RerollStone:
      return "Reroll Stone";
    case RewardType.EliteStone:
      return "Elite Stone";
    case RewardType.NormalRuneStone:
      return "Normal Rune Stone";
    case RewardType.RerollRuneStone:
      return "Reroll RuneS tone";
    case RewardType.EliteRuneStone:
      return "Elite Rune Stone";
    case RewardType.Energy:
      return "Energy";
    case RewardType.Pigment:
      return "Pigment";
    case RewardType.BLESSING:
      return "Blessing";
    case RewardType.EMOJI:
      return "Emoji";
    case RewardType.BATTLEPASS_EXP:
      return "Battlepass EXP";
    case RewardType.ElectiveStoneCO:
    case RewardType.ElectiveStoneUC:
    case RewardType.ElectiveStoneRA:
    case RewardType.ElectiveStoneEP:
    case RewardType.ElectiveStoneLE:
      return "Elective Stone";
    case RewardType.ElectiveRuneStoneCO:
    case RewardType.ElectiveRuneStoneUC:
    case RewardType.ElectiveRuneStoneRA:
    case RewardType.ElectiveRuneStoneEP:
    case RewardType.ElectiveRuneStoneLE:
      return "Elective Rune Stone";
    case RewardType.Rune:
      return reward.rune_code;
    default:
      return "";
  }
}

function getRarityIcon(rarity: any) {
  const baseUrl = "/assets/images/rarity";
  switch (rarity) {
    case Rarity.Common:
      return baseUrl + "/common-icon.svg";
    case Rarity.Uncommon:
      return baseUrl + "/uncommon-icon.svg";
    case Rarity.Rare:
      return baseUrl + "/rare-icon.svg";
    case Rarity.Epic:
      return baseUrl + "/epic-icon.svg";
    case Rarity.Legendary:
      return baseUrl + "/legendary-icon.svg";
  }
}

function generateReferral(id: number) {
  let a = id + 10000000000;
  return `MS${a.toString(36)}`.toUpperCase();
}

function formatHostUrl(hostapi: string) {
  if (hostapi[hostapi.length - 1] === "/") {
    return hostapi.slice(0, -1);
  }
  return hostapi;
}

function displayTime(time: string) {
  //@ts-ignore
  return dayjs(time).utc().format(`h A UTC MMMM DD, YYYY`);
}

function getTimeDisplay(secDif: number) {
  if (secDif < 0) {
    return `0 days 0 hours 0 mins 0 secs`;
  }
  const days = Math.floor(secDif / (24 * 60 * 60));
  secDif = secDif - days * 24 * 60 * 60;
  const hours = Math.floor(secDif / 60 / 60);
  secDif = secDif - hours * 60 * 60;
  const min = Math.floor(secDif / 60);
  secDif = secDif - min * 60;
  const sec = Math.floor(secDif);
  return `${days} days ${hours} hours ${min} mins ${sec} secs`;
}

function getTicketType(rarity: TicketRarity) {
  switch (rarity) {
    case TicketRarity.Common:
      return NFTType.TicketCO;
    case TicketRarity.Uncommon:
      return NFTType.TicketUC;
    case TicketRarity.Rare:
      return NFTType.TicketRA;
    case TicketRarity.Epic:
      return NFTType.TicketEP;
    case TicketRarity.GE:
      return NFTType.TicketGE;
    case TicketRarity.GL:
      return NFTType.TicketGL;
    case TicketRarity.Legendary:
      return NFTType.TicketLE;
    default:
      throw new Error("invalid_type");
  }
}

function getTicketTypeFromUnit(unit: TicketUnit) {
  switch (unit) {
    case "ticket_co":
      return NFTType.TicketCO;
    case "ticket_uc":
      return NFTType.TicketUC;
    case "ticket_ra":
      return NFTType.TicketRA;
    case "ticket_ep":
      return NFTType.TicketEP;
    case "ticket_ge":
      return NFTType.TicketGE;
    case "ticket_gl":
      return NFTType.TicketGL;
    case "ticket_le":
      return NFTType.TicketLE;
    default:
      throw new Error("invalid_type");
  }
}

const getPlotIcon = (reward: RewardEntity) => {
  // @ts-ignore
  if (reward.race === -1 || reward.rarity === -1) {
    return null;
  }

  let rarity = reward.rarity as Rarity;
  let src = `/assets/images/plots/${PlotType[
    reward.plot_type
  ].toLowerCase()}-${rarity}.webp`;

  switch (reward.plot_type) {
    case PlotType.Pasture:
      src = `/assets/images/plots/${PlotType[
        reward.plot_type
      ].toLowerCase()}-${MongenRace[reward.race].toLowerCase()}-${rarity}.webp`;
      break;
    case PlotType.Trap:
      if (reward.sub_plot_type) {
        src = `/assets/images/plots/${TrapType[
          reward.sub_plot_type
        ].toLowerCase()}-${PlotType[
          reward.plot_type
        ].toLowerCase()}-${rarity}.webp`;
      }
      break;
    case PlotType.Bombard:
      if (reward.sub_plot_type) {
        src = `/assets/images/plots/${BombardType[
          reward.sub_plot_type
        ].toLowerCase()}-${PlotType[
          reward.plot_type
        ].toLowerCase()}-${rarity}.webp`;
      }
      break;
    case PlotType.Barracks:
      if (reward.sub_plot_type) {
        src = `/assets/images/plots/${BarrackType[
          reward.sub_plot_type
        ].toLowerCase()}-${PlotType[
          reward.plot_type
        ].toLowerCase()}-${rarity}.webp`;
      }
      break;
    case PlotType.Tower:
      if (reward.sub_plot_type) {
        src = `/assets/images/plots/${TowerType[
          reward.sub_plot_type
        ].toLowerCase()}-${PlotType[
          reward.plot_type
        ].toLowerCase()}-${rarity}.webp`;
      }
      break;
    case PlotType.Camp:
      if (reward.sub_plot_type) {
        src = `/assets/images/plots/${CampType[
          reward.sub_plot_type
        ].toLowerCase()}-${PlotType[
          reward.plot_type
        ].toLowerCase()}-${rarity}.webp`;
      }
      break;
  }
  return src;
};

const getSoulcoreIcon = (reward: RewardEntity) => {
  // @ts-ignore
  if (reward.race === -1 || reward.rarity === -1) {
    return null;
  }
  let src = `/assets/images/egg/${MongenRace[reward.race].toLowerCase()}-${
    reward.rarity
  }.webp`;
  return src;
};

function getWalletToken(user: UserEntity, unit: string) {
  switch (unit) {
    case "MSTR": {
      return user?.mstr_wallet ?? 0;
    }
    case "MAG":
    case "mag": {
      return user?.mag_wallet ?? 0;
    }
    case "USDT": {
      return user?.usdt_wallet ?? 0;
    }
    case "ticket_co": {
      return user?.TICKETCO_wallet ?? 0;
    }
    case "ticket_uc": {
      return user?.TICKETUC_wallet ?? 0;
    }
    case "ticket_ra": {
      return user?.TICKETRA_wallet ?? 0;
    }
    case "ticket_ep": {
      return user?.TICKETEP_wallet ?? 0;
    }
    case "ticket_ge": {
      return user?.TICKETGE_wallet ?? 0;
    }
    case "ticket_gl": {
      return user?.TICKETGL_wallet ?? 0;
    }
    case "ELIXIR": {
      return user?.elixir_wallet ?? 0;
    }
    case "MUTATEGEN": {
      return user?.mutategen_wallet ?? 0;
    }
    default:
      return 0;
  }
}

const getAddressDisplay = (wallets: any) => {
  const loginType = store.getState().logintype;
  let availableAuths = getAvailableAuths(wallets, loginType);
  let wallet =
    availableAuths.length > 0
      ? wallets[availableAuths[0]][AuthInterface.identity]
      : "--";
  return wallets[loginType]?.[AuthInterface.identity] ?? wallet;
};

const getAvailableAuths = (wallets: any, loginType: any) => {
  let availableAuths = Config.SupportedAuth.filter(
    (type) =>
      !!wallets[type] &&
      Object.keys(wallets[type]).length > 0 &&
      loginType != type
  );
  return availableAuths;
};

export const toHex = (num: any): string => {
  const val = Number(num);
  return "0x" + val.toString(16);
};

export function sanitizeHex(hex: string): string {
  hex = hex.substring(0, 2) === "0x" ? hex.substring(2) : hex;
  if (hex === "") {
    return "";
  }
  hex = hex.length % 2 !== 0 ? "0" + hex : hex;
  return "0x" + hex;
}

const getChainFromLogin = (loginType: LoginType) => {
  switch (loginType) {
    case LoginType.BSC:
      return Chain.BSC;
    case LoginType.Terra:
      return Chain.Terra;
    case LoginType.Avalanche:
      return Chain.Avalanche;
    case LoginType.Okex:
      return Chain.Okex;
    case LoginType.Aura:
      return Chain.Aura;
    default:
      return Chain.BSC;
  }
};
const getLoginTypeFromChain = (chain: Chain) => {
  switch (chain) {
    case Chain.BSC:
      return LoginType.BSC;
    case Chain.Terra:
      return LoginType.Terra;
    case Chain.Avalanche:
      return LoginType.Avalanche;
    case Chain.Okex:
      return LoginType.Okex;
    case Chain.Aura:
      return LoginType.Aura;
    default:
      return LoginType.BSC;
  }
};

const isValidEmail = (email: string) => {
  const isValid = email.match(
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  );
  return isValid;
};

function toFixed(x: any) {
  if (Math.abs(x) < 1.0) {
    var e = parseInt(x.toString().split("e-")[1]);
    if (e) {
      x *= Math.pow(10, e - 1);
      x = "0." + new Array(e).join("0") + x.toString().substring(2);
    }
  } else {
    var e = parseInt(x.toString().split("+")[1]);
    if (e > 20) {
      e -= 20;
      x /= Math.pow(10, e);
      x += new Array(e + 1).join("0");
    }
  }
  return x;
}

const getBoxIcon = (reward: RewardEntity) => {
  if (reward.box_name) {
    let boxData: any = Config.BoxData;
    let icon = boxData[reward.box_name]?.image;
    if (icon !== null && icon !== undefined) {
      return `/assets/images/event_box/${icon}.png`;
    }
  }
  return null;
};

const getDisplayLoginType = (type: LoginType) => {
  if (type === null) return "";
  switch (type) {
    case LoginType.BSC:
      return "BNB";
    case LoginType.Okex:
      return "OKX";
    case LoginType.Avalanche:
      return "AVAX";
    default:
      return LoginType[type].toString();
  }
};

function formatName(name: string): string {
  if (!name) {
    return name;
  }
  if (name.includes("<color")) {
    let end = name.indexOf("</color>");
    name = name.slice(end + 8);
  }
  return name;
}

function getItemInfo({
  type,
  plot_type,
  configData,
}: {
  type: NFTType;
  plot_type?: PlotType;
  configData: any[];
}): any {
  return configData.find((i: any) => {
    if (i.type !== type) return false;
    if (plot_type !== undefined && plot_type !== i.plot_type) {
      return false;
    }
    return true;
  });
}

const utils = {
  getDisplayLoginType,
  getBoxIcon,
  getSoulcoreIcon,
  toFixed,
  isValidEmail,
  getLoginTypeFromChain,
  getChainFromLogin,
  sanitizeHex,
  toHex,
  getKeyMap,
  getTicketType,
  getTimeDisplay,
  getRarityName,
  getRarityIcon,
  getRewardName,
  getPlotIcon,
  getWalletToken,
  getExplorerUrl,
  getAddressDisplay,
  displayTime,
  displayWalletAddress,
  displayFloat,
  create_UUID,
  generateReferral,
  formatHostUrl,
  convertToInternationalCurrencySystem,
  getAvailableAuths,
  getTicketTypeFromUnit,
  getTicketRarityName,
  formatName,
  getItemInfo,
};
export default utils;
